/**
 * CommandCentrePalette — ⌘Shift+K / Ctrl+Shift+K keyboard command palette.
 * Jul 2026
 *
 * Fires from anywhere on `/admin`. Fuzzy-search across:
 *   • Section jumps  — every CommandCentreSection anchor
 *   • Tile jumps     — every high-traffic tile (mirrors CommandCentreSubNav)
 *   • Live actions   — "Refresh CI Health from preview",
 *                      "Refresh Pytest Flakes", "Send test Monday Pulse"
 *
 * Note: The plain ⌘K shortcut is already claimed by AdminLayout's global
 * "take me somewhere" search palette. This surface uses the Shift-variant
 * so both coexist — global search for navigation, this palette for
 * Command-Centre actions.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Search, ArrowRight, Send, RefreshCw, Command,
} from 'lucide-react';
import { apiClient } from '../../api/client';

// Same section ids as CommandCentreSubNav.SECTION_JUMPS. Kept flat and
// duplicated here (not imported) so the palette isn't coupled to the
// subnav's internals — if either surface adds a new section, the change
// is one-line-per-file, not a hidden cross-file contract.
const SECTION_TARGETS = [
  { id: 'cc-section-stability-health',        label: 'Stability & Health',      hint: 'Section' },
  { id: 'cc-section-stabilisation',           label: 'Stabilisation Register',  hint: 'Section' },
  { id: 'cc-section-growth-signal',           label: 'Growth Signal',           hint: 'Section' },
  { id: 'cc-section-newsletter-comms',        label: 'Newsletter & Comms',      hint: 'Section' },
  { id: 'cc-section-community-voice',         label: 'Community & Voice',       hint: 'Section' },
  { id: 'cc-section-product-ava',             label: 'Product & AvA',           hint: 'Section' },
  { id: 'cc-section-founder-board',           label: 'Founder & Board',         hint: 'Section' },
  { id: 'cc-section-security-admin',          label: 'Security & Admin',        hint: 'Section' },
  { id: 'cc-section-business-intelligence',   label: 'Business Intelligence',   hint: 'Section' },
  { id: 'cc-section-growth-leads',            label: 'Growth & Leads',          hint: 'Section' },
  { id: 'cc-section-revenue-social-product',  label: 'Revenue · Social · Product', hint: 'Section' },
];

const TILE_TARGETS = [
  { id: 'cc-investor-pulse',    label: 'Investor Pulse',       hint: 'Tile' },
  { id: 'cc-newsletter-ops',    label: 'Newsletter Ops',       hint: 'Tile' },
  { id: 'cc-safety-guardrails', label: 'Safety & Guardrails',  hint: 'Tile' },
  { id: 'cc-ava-behavior',      label: 'AvA Behavior',         hint: 'Tile' },
  { id: 'cc-rebecca-manager',   label: 'Rebecca Tutor',        hint: 'Tile' },
  { id: 'cc-voice-of-user',     label: 'Voice of User',        hint: 'Tile' },
  { id: 'cc-business-glance',   label: 'Business at a Glance', hint: 'Tile' },
  { id: 'cc-user-cohorts',      label: 'User Cohorts',         hint: 'Tile' },
];

// Small helper — the exact scroll-with-sticky-header logic used by the subnav.
function jumpToAnchor(id) {
  const el = document.getElementById(id);
  if (!el) return;
  let stickyBottom = 109; // fallback = HEADER + SUBNAV constants
  try {
    const subnav = document.querySelector('[data-testid="cc-subnav"]');
    if (subnav) stickyBottom = Math.max(0, subnav.getBoundingClientRect().bottom);
  } catch (e) { /* fall back */ }
  const top = el.getBoundingClientRect().top + window.scrollY - (stickyBottom + 12);
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

export const CommandCentrePalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const [actionResult, setActionResult] = useState(null); // { kind: 'ok'|'err', message }
  const inputRef = useRef(null);

  // Live actions — call backends. Wrapped so we can surface a lightweight
  // inline result banner after execution without leaving the palette.
  const runRefreshCIHealth = useCallback(async () => {
    try {
      const res = await apiClient.request('/api/admin/health/tests/run', { method: 'POST' });
      if (res?.status === 'mirrored') {
        setActionResult({ kind: 'ok', message: `Mirrored from ${res.source}` });
      } else if (res?.run_id) {
        setActionResult({ kind: 'ok', message: 'Pytest run started — streaming to CI badge' });
      } else {
        setActionResult({ kind: 'ok', message: 'CI Health refresh triggered' });
      }
    } catch (e) {
      setActionResult({ kind: 'err', message: e?.message || 'CI refresh failed' });
    }
  }, []);

  const runSendMondayPulse = useCallback(async () => {
    try {
      const res = await apiClient.request('/api/admin/weekly-pulse/send-test', { method: 'POST' });
      const pmErr = res?.postmark_result?.error;
      if (pmErr) {
        setActionResult({ kind: 'err', message: `Postmark: ${pmErr.slice(0, 90)}` });
      } else {
        setActionResult({ kind: 'ok', message: `Monday Pulse sent to ${res?.to || 'your inbox'}` });
      }
    } catch (e) {
      setActionResult({ kind: 'err', message: e?.message || 'Send failed' });
    }
  }, []);

  const ACTIONS = useMemo(() => [
    { id: 'action-ci-refresh',    label: 'Refresh CI Health',        hint: 'Action', icon: RefreshCw, run: runRefreshCIHealth },
    { id: 'action-monday-pulse',  label: 'Send test Monday Pulse',   hint: 'Action', icon: Send,      run: runSendMondayPulse },
  ], [runRefreshCIHealth, runSendMondayPulse]);

  // Combined pool — sections + tiles + actions. All entries have
  // { id, label, hint }. Actions additionally carry a `run` fn.
  const ALL = useMemo(() => [
    ...ACTIONS,
    ...SECTION_TARGETS.map(t => ({ ...t, kind: 'jump' })),
    ...TILE_TARGETS.map(t => ({ ...t, kind: 'jump' })),
  ], [ACTIONS]);

  // Filter — case-insensitive substring on label + hint.
  const results = useMemo(() => {
    if (!query.trim()) return ALL;
    const q = query.toLowerCase();
    return ALL.filter(x =>
      x.label.toLowerCase().includes(q) || (x.hint || '').toLowerCase().includes(q),
    );
  }, [ALL, query]);

  // Reset cursor when the filtered list changes.
  useEffect(() => {
    if (cursor >= results.length) setCursor(0);
  }, [results.length, cursor]);

  // Global ⌘Shift+K / Ctrl+Shift+K listener + focus input on open.
  // The plain ⌘K shortcut is already claimed by AdminLayout's global
  // search palette (see /app/frontend/src/components/layout/AdminLayout.js
  // ~L215). We layer the Command-Centre-scoped palette on Shift+K so
  // both surfaces coexist — global search for "take me somewhere",
  // Command-Centre palette for "run this action / jump to this tile".
  useEffect(() => {
    const onKey = (ev) => {
      if ((ev.metaKey || ev.ctrlKey) && ev.shiftKey && (ev.key || '').toLowerCase() === 'k') {
        ev.preventDefault();
        setOpen(prev => !prev);
      } else if (ev.key === 'Escape' && open) {
        ev.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setQuery('');
      setCursor(0);
      setActionResult(null);
    }
  }, [open]);

  const execute = useCallback(async (entry) => {
    if (!entry) return;
    if (entry.run) {
      await entry.run();
      // Keep palette open briefly so the result banner is visible; auto-close after 1.6s.
      setTimeout(() => setOpen(false), 1600);
      return;
    }
    // Jump — close first, then scroll after the modal is out of the way.
    setOpen(false);
    setTimeout(() => jumpToAnchor(entry.id), 60);
  }, []);

  const onKeyDown = (ev) => {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setCursor(c => Math.min(c + 1, results.length - 1));
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setCursor(c => Math.max(c - 1, 0));
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      execute(results[cursor]);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      data-testid="cc-palette-backdrop"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-start justify-center pt-24 px-4"
      style={{ backgroundColor: 'rgba(15, 42, 85, 0.35)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div
        data-testid="cc-palette"
        className="w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden"
        style={{ border: '1px solid #E5E7EB' }}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: '#F3F4F6' }}>
          <Search size={18} style={{ color: '#7C3AED' }} />
          <input
            ref={inputRef}
            data-testid="cc-palette-input"
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setCursor(0); }}
            onKeyDown={onKeyDown}
            placeholder="Jump to a section, tile, or run an action…"
            className="flex-1 outline-none text-sm bg-transparent"
            style={{ color: '#1A1209' }}
          />
          <span
            className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
            style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}
          >
            ⌘⇧K
          </span>
        </div>

        {actionResult && (
          <div
            data-testid="cc-palette-action-result"
            className="px-4 py-2 text-xs font-semibold"
            style={{
              backgroundColor: actionResult.kind === 'ok' ? '#F0FAF3' : '#FEF2F2',
              color: actionResult.kind === 'ok' ? '#059669' : '#DC2626',
            }}
          >
            {actionResult.message}
          </div>
        )}

        <div className="max-h-80 overflow-y-auto py-1">
          {results.length === 0 && (
            <div className="px-4 py-6 text-xs text-center" style={{ color: '#9CA3AF' }}>
              No matches — try &ldquo;monday&rdquo;, &ldquo;ci&rdquo;, or a section name.
            </div>
          )}
          {results.map((r, i) => {
            const isActive = i === cursor;
            const Icon = r.icon || ArrowRight;
            return (
              <button
                key={r.id}
                data-testid={`cc-palette-item-${r.id}`}
                onMouseEnter={() => setCursor(i)}
                onClick={() => execute(r)}
                className="w-full flex items-center justify-between gap-3 px-4 py-2 text-left transition-colors"
                style={{
                  backgroundColor: isActive ? '#F5F3FF' : 'transparent',
                  color: '#1A1209',
                }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Icon size={14} style={{ color: isActive ? '#7C3AED' : '#6B7280' }} />
                  <span className="text-sm font-medium truncate">{r.label}</span>
                </div>
                <span
                  className="text-[10px] font-semibold uppercase tracking-wider shrink-0"
                  style={{ color: isActive ? '#7C3AED' : '#9CA3AF' }}
                >
                  {r.hint}
                </span>
              </button>
            );
          })}
        </div>

        <div
          className="flex items-center justify-between gap-3 px-4 py-2 border-t text-[10px]"
          style={{ borderColor: '#F3F4F6', color: '#9CA3AF' }}
        >
          <span className="flex items-center gap-3">
            <span><Command size={10} className="inline mr-1" />↑↓ navigate</span>
            <span>↵ execute</span>
            <span>Esc close</span>
          </span>
          <span>{results.length} result{results.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CommandCentrePalette;
